<template>
  <div class="customer-create-dialog modal-footer">
    <SDialog v-model:open="modalVisibility">
      <SDialogContent
        data-cy="customer-create-dialog"
        class="customer-create-dialog !w-[80%] !max-w-[80%]"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @focus-outside.prevent
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle>
            <h2>{{ $t("customer.title") }}</h2>
          </SDialogTitle>
          <SSeparator class="mb-0" />
        </SDialogHeader>

        <el-scrollbar :max-height="maxHeight">
          <CustomerCreateForm ref="customerCreateForm"></CustomerCreateForm>
        </el-scrollbar>

        <SDialogFooter class="gap-3">
          <SButton
            type="button"
            variant="outline"
            class="min-w-[120px]"
            :loading="isLoading"
            data-cy="customer-create-save-button"
            @click="onSave()"
          >
            {{ $t("button.save") }}
          </SButton>
          <SButton
            type="button"
            class="min-w-[120px]"
            :loading="isLoading"
            data-cy="customer-create-save-select-button"
            @click="onSave(true)"
          >
            {{ $t("button.save-select") }}
          </SButton>
        </SDialogFooter>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";

// modules
import CommonModule from "@/store/modules/common";
import UserModule from "@/store/modules/user";
import CustomersModule from "@/store/modules/customers";
import AppModule from "@/store/modules/app";

// helpers
import { dialogVisibleForStatus, hideDialog } from "@/utils/vue-helpers/modal";
import { convertIdToCodeLang } from "@/utils/helpers/lang";
import NotificationService from "@/services/notification.service";

// constants
import { ModalStatus } from "@/constants/modal";
import { ICustomer } from "@/interfaces/responses/customer";

import { useI18n } from "vue-i18n";
import { useOrderStore } from "@smeetz/order-store";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SSeparator,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "CustomerCreate",
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
    SSeparator,
  },
  setup() {
    const modalVisibility = dialogVisibleForStatus(ModalStatus.CustomerCreate);
    let isLoading = ref<boolean>(false);
    const customerCreateForm = ref();
    const maxHeight = "50vh";

    const { t } = useI18n({});

    CommonModule.fetchCountries({ lang: AppModule.language });

    const onSave = async (isSelect?: boolean) => {
      if (!customerCreateForm.value) return;
      isLoading.value = true;

      const { valid, values } = await customerCreateForm.value.validate();

      if (!valid) {
        isLoading.value = false;
        NotificationService.showCustomMessage(
          t("error.missing-fields"),
          "warning"
        );
        return;
      }

      const { language, ...data } = values;
      const params = {
        groupId: UserModule.getUserGroupId,
        ...data,
        language: convertIdToCodeLang(language),
      };

      let newCustomer = await CustomersModule.postCustomer(params);

      if (isSelect && newCustomer) {
        const {
          country,
          city,
          email,
          firstName,
          lastName,
          phone,
          id,
          company,
          address,
          allergies,
          language,
          title,
          zipCode,
          emailMarketingOrganiser,
          birthDate,
        } = newCustomer as ICustomer;

        // TODO: Only one customer per booking for now
        const orderStore = useOrderStore();
        await CustomersModule.setSelectedCustomers([
          {
            country,
            city,
            email,
            fullName: `${firstName} ${lastName}`,
            phone,
            id: String(id),
            checkOrder: true,
            orderId: orderStore.selectedOrder?.id,
            company,
            address,
            allergies,
            language,
            title,
            zipCode,
            emailMarketingOrganiser,
            birthDate,
          },
        ]);
      }

      CustomersModule.getCustomersList.push(newCustomer);

      isLoading.value = false;
      hideDialog();
    };

    return {
      modalVisibility,
      isLoading,
      customerCreateForm,
      maxHeight,
      hideDialog,
      onSave,
      t,
    };
  },
});
</script>

<style lang="scss"></style>
